// Making sure the owner may write the institute's files in Cloud Storage.
//
// Storage rules allow writes under orgs/{orgId}/ only when the ID token carries
// an `orgId` claim naming that institute. The API sets the claim when an
// institute is created, but a token minted before then lacks it, and owners who
// signed up before the claim existed have none at all. So every Storage write
// goes through withStorageAccess: once per session it asks the API to ensure
// the claim, and refreshes the token when the API says the one we hold is stale.

import { auth } from '../config/firebase';
import { orgApi } from './api';

export const STORAGE_DENIED_MESSAGE =
  "You don't have permission to change this institute's files. Sign out, sign back in and try again. If it keeps happening, contact Prepgen support.";

// One check per user and institute per page load, shared by concurrent writes.
const checks = new Map();

export function ensureStorageAccess(orgId) {
  const key = `${auth.currentUser?.uid}:${orgId}`;
  if (!checks.has(key)) {
    const check = orgApi.ensureStorageAccess(orgId).then(async (res) => {
      if (res.data?.refreshRequired) {
        await auth.currentUser?.getIdToken(true);
      }
    });
    // Forget a failed check so the next write asks again.
    check.catch(() => checks.delete(key));
    checks.set(key, check);
  }
  return checks.get(key);
}

// Run a Storage write for an institute once its owner's token can make it.
// A permission failure becomes an Error whose message the UI can show as is.
export async function withStorageAccess(orgId, write) {
  try {
    await ensureStorageAccess(orgId);
  } catch (err) {
    // The token may already carry the claim, so let the rules decide.
    console.warn('Could not confirm storage access:', err);
  }
  try {
    return await write();
  } catch (err) {
    if (err?.code === 'storage/unauthorized') {
      const denied = new Error(STORAGE_DENIED_MESSAGE);
      denied.code = err.code;
      throw denied;
    }
    throw err;
  }
}
